import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";
import { LegalBody, LegalSection } from "@/components/LegalLayout";
import { canonicalFor } from "@/lib/site";

export default function Terms() {
  return (
    <>
      <Seo
        title="Terms | Crestline Roofing Ltd."
        description="The terms of use that govern this website and the roofing services Crestline Roofing Ltd. provides across Vancouver and the Fraser Valley, BC."
        canonical={canonicalFor("/terms")}
      />
      <PageLayout>
        {() => (
          <main data-testid="terms-page" className="bg-[var(--crest-charcoal)] text-white">
            <PageHero
              eyebrow="Legal"
              title="Terms of Service"
              subtitle="The terms that govern your use of this website and the roofing services Crestline Roofing Ltd. provides across Vancouver and the Fraser Valley, BC."
              image="https://images.unsplash.com/photo-1583345237708-add35a664d77?crop=entropy&cs=srgb&fm=webp&q=85&w=2400"
              imageAlt="Steep residential rooftop at golden hour — Crestline Roofing Ltd. Vancouver"
              breadcrumb={[{ label: "Home", href: "/" }, { label: "Terms of Service" }]}
            />

            <LegalBody updated="June 2026">
              <LegalSection heading="Use of this website">
                <p>
                  By accessing or using crestlineroofing.ca (the &ldquo;Site&rdquo;), you
                  agree to these Terms of Service. If you do not agree, please
                  do not use this Site.
                </p>
                <p>
                  You may use this Site to learn about Crestline Roofing Ltd.,
                  request a free estimate, contact us, and review our work.
                  You agree to use the Site only for lawful purposes and in a
                  way that does not infringe the rights of, restrict, or
                  inhibit anyone else&apos;s use of it.
                </p>
                <p>
                  All content on this Site — including text, photography,
                  graphics, the Crestline Roofing Ltd. name and logo, and the
                  overall design — is the property of Crestline Roofing Ltd.
                  or its licensors and is protected by Canadian copyright and
                  trademark law. You may not reproduce, redistribute, or use
                  any content from this Site for commercial purposes without
                  our prior written consent.
                </p>
              </LegalSection>

              <LegalSection heading="Service area">
                <p>
                  Crestline Roofing Ltd. provides roofing installation,
                  repair, inspection, and emergency services in{" "}
                  <strong>Vancouver and the Fraser Valley, British Columbia</strong>,
                  including (but not limited to) Burnaby, North Vancouver, West
                  Vancouver, Richmond, Surrey, Coquitlam, Port Moody, New
                  Westminster, Delta, Langley, Maple Ridge, Pitt Meadows,
                  Abbotsford, and Mission.
                </p>
                <p>
                  Service availability in any specific location is subject to
                  scheduling, accessibility, and crew capacity. If you&apos;re
                  unsure whether we serve your neighbourhood, please call us at{" "}
                  <a href="tel:+17789176817">(778) 917-6817</a>.
                </p>
              </LegalSection>

              <LegalSection heading="Estimates and quotes">
                <p>
                  All estimates provided by Crestline Roofing Ltd. are{" "}
                  <strong>free and non-binding</strong> until a written
                  contract is signed by both parties. Requesting an estimate
                  through this Site, by phone, or by email does not create any
                  obligation on your part to hire us, or on our part to perform
                  work.
                </p>
                <p>
                  Estimates are valid for 30 days from the date issued unless
                  otherwise specified in writing. Pricing may be adjusted in
                  the event of material cost changes, hidden deck damage
                  discovered after tear-off, or scope changes requested by you
                  — and any such adjustments will be documented in writing and
                  approved by you before additional work proceeds.
                </p>
              </LegalSection>

              <LegalSection heading="Workmanship and warranties">
                <p>
                  Where applicable, Crestline Roofing Ltd. provides a
                  written workmanship warranty on completed installation work.
                  The specific term, coverage, and any exclusions of the
                  workmanship warranty are stated in writing on each individual
                  project contract.
                </p>
                <p>
                  Manufacturer warranties on materials (e.g. asphalt shingles,
                  metal panels, underlayments) are provided directly by the
                  manufacturer and are governed by their own terms, which we
                  will share with you in writing as part of your project
                  paperwork.
                </p>
              </LegalSection>

              <LegalSection heading="Limitation of liability">
                <p>
                  This Site and its content are provided on an &ldquo;as is&rdquo;
                  and &ldquo;as available&rdquo; basis. While we make every
                  reasonable effort to keep the information on this Site
                  accurate and up to date, we make no warranties or
                  representations about the completeness, accuracy, reliability,
                  or suitability of the information for any particular purpose.
                </p>
                <p>
                  To the fullest extent permitted by applicable law, Crestline
                  Roofing Ltd. and its owners, employees, and contractors
                  shall not be liable for any indirect, incidental, special,
                  consequential, or punitive damages arising from your use of
                  this Site or your reliance on any information provided here.
                </p>
                <p>
                  Nothing in these Terms limits our liability for work
                  performed under a signed project contract — that liability is
                  governed by the terms of that contract and by applicable
                  British Columbia law.
                </p>
              </LegalSection>

              <LegalSection heading="Third-party links">
                <p>
                  This Site may contain links to third-party websites (for
                  example, manufacturer information pages or accreditation
                  bodies). We do not control those websites and are not
                  responsible for their content, practices, or privacy
                  policies. Following a third-party link is at your own risk.
                </p>
              </LegalSection>

              <LegalSection heading="Governing law">
                <p>
                  These Terms of Service are governed by and construed in
                  accordance with the laws of the{" "}
                  <strong>Province of British Columbia, Canada</strong>, and the
                  federal laws of Canada applicable therein, without regard to
                  conflict-of-law principles.
                </p>
                <p>
                  Any dispute arising out of or relating to these Terms or your
                  use of this Site shall be brought exclusively in the courts
                  of competent jurisdiction located in the Province of British
                  Columbia, and you consent to the exclusive jurisdiction and
                  venue of those courts.
                </p>
              </LegalSection>

              <LegalSection heading="Changes to these Terms">
                <p>
                  We may update these Terms of Service from time to time. Any
                  changes will be posted on this page with a revised &ldquo;Last
                  updated&rdquo; date. Your continued use of the Site after
                  changes are posted constitutes your acceptance of the
                  updated Terms.
                </p>
              </LegalSection>

              <LegalSection heading="Contact">
                <p>Questions about these Terms? Get in touch:</p>
                <ul>
                  <li><a href="tel:+17789176817">(778) 917-6817</a></li>
                  <li><a href="mailto:info@crestlineroofing.ca">info@crestlineroofing.ca</a></li>
                  <li>Crestline Roofing Ltd. — Lower Mainland, British Columbia</li>
                </ul>
              </LegalSection>
            </LegalBody>
          </main>
        )}
      </PageLayout>
    </>
  );
}
